import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { API_URL } from "../../../config";
import "./AdminSalaryPage.css";

function AdminSalaryPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(
    new Date().toLocaleString("en-US", { month: "long" })
  );
  const [year, setYear] = useState(new Date().getFullYear());
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchRows();
  }, [month, year]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/salary/all?month=${month}&year=${year}`);
      setRows(res.data || []);
    } catch (err) {
      console.error("fetchRows:", err);
      setRows([]);
    }
    setLoading(false);
  };

  const generateForEmployee = async (empDbId) => {
    if (!window.confirm("Generate salary for this employee?")) return;
    try {
      await axios.post(`${API_URL}/api/generateSalary/${empDbId}`, { month, year });
      fetchRows();
    } catch (err) {
      console.error("generateForEmployee:", err);
      alert("Error generating salary: " + (err.response?.data?.message || err.message));
    }
  };

  const regenSalary = async (empId) => {
    if (!window.confirm("Re-generate salary?")) return;
    try {
      await axios.put(`${API_URL}/api/salary/regen/${empId}`, { month, year });
      fetchRows();
    } catch (err) {
      console.log("regenSalary:", err);
      alert("Error re-generating salary: " + (err.response?.data?.message || err.message));
    }
  };

  const markPaid = async (salaryId) => {
    if (!window.confirm("Mark this salary as Paid?")) return;
    try {
      await axios.put(`${API_URL}/api/salary/update/${salaryId}`, { status: "Paid" });
      fetchRows();
    } catch (err) {
      console.error("markPaid:", err);
      alert("Error updating salary status");
    }
  };

  const openDetails = (row) => {
    setSelected(row);
    setShowModal(true);
  };

  const filtered = rows.filter((r) => {
    const name = (r.employeeId?.ename || r.employeeName || "").toLowerCase();
    const empCode = (r.employeeId?.employeeId || r.employeeCode || r.empCode || "").toLowerCase();
    const q = search.toLowerCase();
    return name.includes(q) || empCode.includes(q);
  });

  const stats = useMemo(() => {
    const total = filtered.length;
    const paid = filtered.filter((r) => r.status === "Paid").length;
    const pending = filtered.filter((r) => r.status === "Pending").length;
    const notGenerated = filtered.filter((r) => r.status === "Not Generated").length;
    return { total, paid, pending, notGenerated };
  }, [filtered]);

  const statusChip = (status) => {
    if (status === "Paid") return <span className="as-chip success">Paid</span>;
    if (status === "Pending") return <span className="as-chip warning">Pending</span>;
    return <span className="as-chip danger">Not Generated</span>;
  };

  const exportExcel = () => {
    const rowsForExport = filtered.map((r) => ({
      Employee: r.employeeName || "N/A",
      Employee_ID: r.employeeCode || r.empCode || "-",
      Month: `${r.month} ${r.year}`,
      Basic_Pay: Number(r.basicPay || r.employeeId?.givenSalary || 0),
      Attendance: `P:${r.totalPresent} | A:${r.totalAbsent} | HD:${r.totalHalfDays} | PL:${r.paidLeaves} | UPL:${r.unpaidLeaves} | SL:${r.shortLeaves || 0}`,
      Net_Salary: Number(r.netPay || 0),
    }));

    const sheet = XLSX.utils.json_to_sheet(rowsForExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Salaries");
    XLSX.writeFile(workbook, `Salary_${month}_${year}.xlsx`);
  };

  return (
    <div className="as-page">
      <section className="as-hero">
        <div>
          <p className="as-kicker">Finance Control</p>
          <h2>Salary Dashboard</h2>
          <p className="as-sub">Manage payroll generation, status updates, and detailed breakdowns.</p>
        </div>
      </section>

      <section className="as-panel">
        <div className="as-toolbar">
          <div className="as-toolbar-left">
            <select value={month} onChange={(e) => setMonth(e.target.value)} className="form-select">
              {[
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
              ].map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>

            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="form-select">
              {[2023, 2024, 2025, 2026].map((y) => (
                <option key={y}>{y}</option>
              ))}
            </select>
          </div>

          <div className="as-toolbar-right">
            <input
              className="form-control"
              placeholder="Search employee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn as-export-btn" onClick={exportExcel}>Export Excel</button>
          </div>
        </div>

        <div className="as-stats">
          <article className="as-stat">
            <p>Total Rows</p>
            <h4>{stats.total}</h4>
          </article>
          <article className="as-stat">
            <p>Paid</p>
            <h4>{stats.paid}</h4>
          </article>
          <article className="as-stat">
            <p>Pending</p>
            <h4>{stats.pending}</h4>
          </article>
          <article className="as-stat">
            <p>Not Generated</p>
            <h4>{stats.notGenerated}</h4>
          </article>
        </div>

        <div className="as-table-wrap">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Month</th>
                <th>Basic Pay</th>
                <th>Attendance</th>
                <th>Net Salary</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    No records
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading &&
                filtered.map((r, idx) => (
                  <tr key={idx}>
                    <td>
                      <strong>{r.employeeName || "N/A"}</strong>
                      <br />
                      <small className="text-muted">{r.employeeCode || r.empCode || "-"}</small>
                    </td>
                    <td>
                      {r.month} {r.year}
                    </td>
                    <td>Rs {Number(r.basicPay || r.employeeId?.givenSalary || 0).toFixed(2)}</td>
                    <td>
                      P:{r.totalPresent} | A:{r.totalAbsent} | HD:{r.totalHalfDays} | PL:{r.paidLeaves} | UPL:
                      {r.unpaidLeaves} | SL:{r.shortLeaves || 0}
                    </td>
                    <td>Rs {Number(r.netPay || 0).toFixed(2)}</td>
                    <td>{statusChip(r.status)}</td>
                    <td>
                      {r.status === "Not Generated" && (
                        <button
                          className="btn btn-sm as-action-btn generate"
                          onClick={() => generateForEmployee(r.empDbId || r.employeeId?._id)}
                        >
                          Generate Salary
                        </button>
                      )}

                      {r.status !== "Not Generated" && (
                        <div className="d-flex gap-2 flex-wrap">
                          <button className="btn btn-sm as-action-btn view" onClick={() => openDetails(r)}>
                            View Details
                          </button>

                          {r.status !== "Paid" && (
                            <button className="btn btn-sm as-action-btn paid" onClick={() => markPaid(r.salaryId || r._id)}>
                              Mark Paid
                            </button>
                          )}

                          <button className="btn btn-sm as-action-btn regen" onClick={() => regenSalary(r.empDbId || r.employeeId?._id)}>
                            Re-Generate
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      {showModal && selected && (
        <div className="as-modal-overlay">
          <div className="as-modal">
            <div className="as-modal-head">
              <h5>Salary Details - {selected.employeeName || selected.employeeId?.ename || "N/A"}</h5>
              <button className="btn-close" onClick={() => setShowModal(false)} />
            </div>
            <div className="as-modal-body">
              <p><strong>ID:</strong> {selected.employeeCode || selected.employeeId?.employeeId || selected.empCode || "-"}</p>
              <p><strong>Month:</strong> {selected.month} {selected.year}</p>

              <h6>Attendance</h6>
              <p>
                P:{selected.totalPresent} | A:{selected.totalAbsent} | HD:{selected.totalHalfDays} | PL:
                {selected.paidLeaves} | UPL:{selected.unpaidLeaves} | SL:{selected.shortLeaves || 0} | WH:{selected.totalWorkingHours} | Late:
                {selected.lateDays}
              </p>

              <h6>Salary Breakdown</h6>
              <table className="table table-sm">
                <tbody>
                  <tr><th>Basic</th><td>Rs {Number(selected.basicPay || 0).toFixed(2)}</td></tr>
                  <tr><th>Deductions (incl SL)</th><td>Rs {Number(selected.deductions || 0).toFixed(2)}</td></tr>
                  {selected.deductionDetails?.shortLeave > 0 && (
                     <tr><th>└ Short Leave Deduction</th><td>Rs {Number(selected.deductionDetails.shortLeave || 0).toFixed(2)}</td></tr>
                  )}
                  <tr className="as-net-row"><th>Net</th><td>Rs {Number(selected.netPay || 0).toFixed(2)}</td></tr>
                  <tr><th>Status</th><td>{selected.status}</td></tr>
                </tbody>
              </table>
            </div>
            <div className="as-modal-foot">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSalaryPage;
